import sentinelLogo from '../assets/logo.png'
import powerfulLogo from '../assets/whitelabel-logo.png'

// White-label branding model. A `Brand` bundles every per-tenant surface the
// dashboard can swap at runtime: the logo shown in the sidebar/footer, the
// document title + favicon, the footer legal/company copy, and the
// `data-brand` attribute that flips the CSS-variable colour theme (see
// src/globals/theme.scss). Colours themselves live in CSS variables keyed by
// `dataBrand` rather than here, so SCSS consumers pick them up automatically.
export interface Brand {
    key: string
    // Value written to <html data-brand="..."> to select the CSS colour theme.
    dataBrand: string
    logo: string
    logoAlt: string
    // Browser tab title.
    title: string
    // Favicon href applied at runtime. Sentinel keeps the static /favicon.ico;
    // white-label brands point at their bundled logo until a dedicated square
    // icon is supplied.
    faviconHref: string
    // Footer copy, one <p> per line.
    footerText: string[]
}

export const SENTINEL_BRAND: Brand = {
    key: 'sentinel',
    dataBrand: 'sentinel',
    logo: sentinelLogo,
    logoAlt: 'Sentinel Legal',
    title: 'Sentinel Legal',
    faviconHref: '/favicon.ico',
    footerText: [
        'Sentinel Legal Ltd is Registered in England and Wales. Registered office Harley House, 29 Cambray Place, Cheltenham, England, GL50 1JN. Authorised and regulated by the Solicitors Regulation Authority.',
        'SRA number 811792. Phone: 0161 528 9544',
    ],
}

export const POWERFUL_BRAND: Brand = {
    key: 'powerful',
    dataBrand: 'powerful',
    logo: powerfulLogo,
    logoAlt: 'Powerful - Fair Claim Path',
    title: 'Powerful',
    // Placeholder: reuses the wordmark logo until a dedicated square favicon is
    // provided.
    faviconHref: powerfulLogo,
    // Placeholder copy — update with Powerful's registered entity and
    // regulatory wording once confirmed.
    footerText: [
        `No Win, No Fee*   FCA Authorised • FRN: 1013306
        Est. Motor Finance Claims,
        Powerful Fair Claim Path is a trading style of First for Justice Ltd. Registered Office: Clavering House, Clavering Place, Newcastle Upon Tyne, NE1 3NG. Company Registration No: 15090914. 
        First for Justice Ltd is authorised and regulated by the Financial Conduct Authority for regulated claims management services. FCA Permission Reference Number: 1013306. Verify at register.fca.org.uk. 
        *No Win, No Fee means you pay nothing unless your claim is successful. 
        If successful, a Success Fee of 15%–30% excl. VAT applies (depending on amount recovered), subject to a maximum of £10,000 excl. VAT. A cancellation fee may apply if you cancel after the 14-day cooling-off period. You are not required to use a Claims Management Company. You may submit a complaint directly to your lender or the Financial Ombudsman Service free of charge.`,
    ],
}

// Organisations that should render a white-label brand. Anything not listed
// falls back to Sentinel.
const ORG_BRAND_MAP: Record<string, Brand> = {
    'PCP Referral': POWERFUL_BRAND,
    'PCP Referral Engagement': POWERFUL_BRAND,
}

export const getBrandForOrg = (org?: string | null): Brand => (org && ORG_BRAND_MAP[org]) || SENTINEL_BRAND

// Dedicated white-label hostnames. Used for pre-login screens (e.g. the footer
// shown before the Cognito redirect) where no organisation is known yet, and to
// guarantee a consistent brand on a domain that exists solely for that
// white-label. Hostname match takes precedence over the org-based brand.
const HOSTNAME_BRAND_MAP: Record<string, Brand> = {
    'dashboard.claimsdirectgroup.com': POWERFUL_BRAND,
}

export const getBrandForHostname = (hostname?: string | null): Brand =>
    (hostname && HOSTNAME_BRAND_MAP[hostname]) || SENTINEL_BRAND
